import { useRef, useState } from "react";
import ThemeButton from "../../../components/theme/themeButton";
import noImage from "../../../assests/tipperUiSection/toptipper-logo.png";
import styles from "./rating.module.scss";

type SubmittingAction = "stars" | "feedback" | null;

const STAR_PATH =
  "M16.000 1.000 L19.233 11.550 L30.266 11.365 L21.231 17.700 L24.817 28.135 L16.000 21.500 L7.183 28.135 L10.769 17.700 L1.734 11.365 L12.767 11.550 Z";

const FILLED_STAR_COLOR = "#2549E8";
const EMPTY_STAR_COLOR = "#c4c4c4";

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg
    viewBox="0 0 32 32"
    width="32"
    height="32"
    aria-hidden="true"
    shapeRendering="geometricPrecision"
    className={styles.star}
  >
    <path
      d={STAR_PATH}
      fill={filled ? FILLED_STAR_COLOR : EMPTY_STAR_COLOR}
      style={{ fill: filled ? FILLED_STAR_COLOR : EMPTY_STAR_COLOR }}
    />
  </svg>
);

interface RatingProps {
  data: {
    theme_color?: string;
    employee_media?: string;
    media?: string;
    org_name?: string;
    property_name?: string;
    type?: string;
    message?: string;
  };
  isTippingDisabled?: boolean;
  hideTipActions?: boolean;
  onSendTip: () => void;
  onSubmitRating: (
    rating: number,
    comment: string,
    options?: { includeReviewUuid?: boolean }
  ) => Promise<string>;
}

const TOTAL_STARS = 5;
const HIGH_RATING_THRESHOLD = 3;
const MAX_FEEDBACK_LENGTH = 1000;

const Rating = ({
  data,
  isTippingDisabled = false,
  hideTipActions = false,
  onSendTip,
  onSubmitRating,
}: RatingProps) => {
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [hasSubmittedRating, setHasSubmittedRating] = useState(false);
  const [submittingAction, setSubmittingAction] =
    useState<SubmittingAction>(null);
  const hasSubmittedRatingRef = useRef(false);
  const starSubmitPromiseRef = useRef<Promise<boolean> | null>(null);

  const displayName =
    data?.org_name && data?.type === "1"
      ? data.org_name
      : data?.property_name || data?.org_name || "";

  const themeColor = data?.theme_color || "#2549e8";
  const activeStar = hoveredStar ?? selectedRating ?? 0;
  const isHighRating =
    selectedRating !== null && selectedRating >= HIGH_RATING_THRESHOLD;
  const isLowRating =
    selectedRating !== null && selectedRating < HIGH_RATING_THRESHOLD;
  // Lock stars for super-admin stop / paused org, an in-flight request,
  // or after feedback success. tipping_view Disabled still allows rating.
  const isStarsDisabled =
    isTippingDisabled || submittingAction !== null || !!successMessage;
  // Feedback field & follow-up buttons stay interactive while the rating API loads;
  // disable only after feedback (or "Not Today") has been successfully submitted.
  const isFeedbackSubmitted = !!successMessage;
  const isFeedbackTooLong = feedback.length > MAX_FEEDBACK_LENGTH;

  const waitForPendingStarSubmit = async () => {
    if (starSubmitPromiseRef.current) {
      await starSubmitPromiseRef.current;
    }
  };

  const handleStarClick = async (star: number) => {
    if (isStarsDisabled) return;
    // Skip only when re-selecting the same rating that already succeeded
    if (selectedRating === star && hasSubmittedRating) return;

    setSelectedRating(star);
    setFeedback("");
    setSuccessMessage("");
    setSubmittingAction("stars");

    const includeReviewUuid = hasSubmittedRatingRef.current;
    const submitPromise = (async () => {
      try {
        await onSubmitRating(star, "", { includeReviewUuid });
        hasSubmittedRatingRef.current = true;
        setHasSubmittedRating(true);
        return true;
      } catch {
        // Error toast is shown by the parent handler
        return false;
      }
    })();

    starSubmitPromiseRef.current = submitPromise;

    try {
      await submitPromise;
    } finally {
      if (starSubmitPromiseRef.current === submitPromise) {
        starSubmitPromiseRef.current = null;
      }
      setSubmittingAction(null);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (
      selectedRating === null ||
      isFeedbackSubmitted ||
      isFeedbackTooLong ||
      submittingAction === "feedback"
    ) {
      return;
    }

    setSubmittingAction("feedback");
    try {
      await waitForPendingStarSubmit();
      if (!hasSubmittedRatingRef.current) {
        // Initial star submit failed — nothing to attach the comment to
        return;
      }
      const message = await onSubmitRating(selectedRating, feedback, {
        includeReviewUuid: true,
      });
      setSuccessMessage(message);
    } catch {
      // Error toast is shown by the parent handler
    } finally {
      setSubmittingAction(null);
    }
  };

  const handleSendTip = async () => {
    if (isFeedbackSubmitted) return;
    await waitForPendingStarSubmit();
    onSendTip();
  };

  const handleNotToday = async () => {
    if (isFeedbackSubmitted) return;
    await waitForPendingStarSubmit();
    setSuccessMessage("Thank you for your feedback.");
  };

  return (
    <div className={styles.ratingContainer}>
      <div className={styles.logoWrapper}>
        <div
          className={styles.circle}
          style={{ backgroundColor: data?.theme_color }}
        >
          <img
            src={data?.employee_media || data?.media || noImage}
            alt=""
          />
        </div>
      </div>

      {displayName && <h5 className={styles.orgName}>{displayName}</h5>}

      <p className={styles.question}>
        How would you rate your service today?
      </p>

      <div
        className={styles.stars}
        onMouseLeave={() => {
          if (!isStarsDisabled) setHoveredStar(null);
        }}
      >
        {Array.from({ length: TOTAL_STARS }, (_, index) => {
          const starValue = index + 1;
          const isFilled = starValue <= activeStar;

          return (
            <button
              key={starValue}
              type="button"
              data-star={starValue}
              className={styles.starButton}
              aria-label={`Rate ${starValue} out of ${TOTAL_STARS} stars`}
              disabled={isStarsDisabled}
              onMouseEnter={() => {
                if (!isStarsDisabled) setHoveredStar(starValue);
              }}
              onFocus={() => {
                if (!isStarsDisabled) setHoveredStar(starValue);
              }}
              onBlur={() => setHoveredStar(null)}
              onClick={() => handleStarClick(starValue)}
            >
              <StarIcon filled={isFilled} />
            </button>
          );
        })}
      </div>

      {isTippingDisabled && data?.message && (
        <div className="tippingEnabled">{data.message}</div>
      )}

      {isHighRating && hideTipActions && (
        <div className={styles.followUp}>
          <p className={styles.successMessage}>Thanks for your feedback!</p>
        </div>
      )}

      {isHighRating && !hideTipActions && (
        <div className={styles.followUp}>
          <p className={styles.tipMessage}>
            Thanks for your feedback, would you like to send a tip to thank your
            service workers?
          </p>
          <div className={styles.buttonRow}>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={isFeedbackSubmitted}
              onClick={handleNotToday}
            >
              Not Today
            </button>
            <ThemeButton
              type="button"
              className={styles.primaryButton}
              style={{ backgroundColor: themeColor }}
              disabled={isFeedbackSubmitted}
              onClick={handleSendTip}
            >
              Send a Tip
            </ThemeButton>
          </div>
          {successMessage && (
            <p className={styles.successMessage}>{successMessage}</p>
          )}
        </div>
      )}

      {isLowRating && (
        <div className={styles.followUp}>
          <p className={styles.feedbackMessage}>
          We’re sorry that you had a poor experience. Your feedback is very important to us. Please tell us more about your visit. Please include your contact information if you would like to be contacted by the manager.
          </p>
          <textarea
            className={styles.feedbackInput}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={`Share details of your experience at ${displayName}`}
            rows={5}
            disabled={isFeedbackSubmitted}
          />
          {isFeedbackTooLong && (
            <p className={styles.feedbackError}>
            Please keep your feedback under 1,000 characters.
            </p>
          )}
          <ThemeButton
            type="button"
            className={styles.submitButton}
            style={{ backgroundColor: themeColor }}
            loading={submittingAction === "feedback"}
            disabled={
              isFeedbackSubmitted ||
              isFeedbackTooLong ||
              submittingAction === "feedback"
            }
            onClick={handleFeedbackSubmit}
          >
            Submit
          </ThemeButton>
          {successMessage && (
            <p className={styles.successMessage}>{successMessage}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Rating;
